import { ClientEvent, EventType, UserEvent, SetPresence } from "matrix-js-sdk";
import { useCallback, useEffect, useState } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import { UserKeys } from "src/constants/userDetails";
import { CREATE_GROUP_CHAT, isChatActionAllowed } from "src/constants/permissions"; 

const SLIDING_SYNC_MODE = "sliding";

/**
 * as of writing, other `presence` properties have no usage so we will only store the uuids of online users in an array
 * @param {MatrixClient} matrixClient 
 * @param {boolean} isInitialSyncComplete
 * @param {Object} headers
 * @param {Object} userContext
 * @param {(string) => void} retrieveUsersList
 * @param {string} selectedChatListTypeOrLeadershipUuid
 * @returns {{
 *      onlineUsers: string[]
 *      currentUserOnlineStatus: boolean
 * }} - online users' Matrix IDs, and whether the current user is online
 */

export const usePresenceListener = (matrixClient, isInitialSyncComplete, headers, userContext, handleSettingCurrentUserStatus) => {
    const [ onlineUsersMatrixId, setOnlineUsersMatrixId ] = useState([]); // see JSDoc above

    
    /*  This is supposed to handle the current user's online status on page load.
    **  This would basically set own user presence status based on saved value from database.
    **  If the user does not have any presence status set, we'll default to sending "online" to matrix servers
    */
    const initializePresence = async () => {

        const override = userContext[UserKeys.CHAT_PRESENCE_OVERRIDE];
        if (override === SetPresence.Online || override === SetPresence.Offline) {
            await handleSetOwnUserPresence(override);
        } else {
            await handleSetOwnUserPresence(SetPresence.Online);
        }
    };

    /**
     * This useEffect is responsible for registering presence-related event listeners on 
     * The Matrix client same on the existing presence listeners.
     * This one will automatically handles cleanup when the component unmounts or when "matrixClient" changes.
     * emoveListener to prevents memory leaks and duplicate event handling by removing the exact same function reference used in ".on(...)".
     * Ensures that listeners are not registered multiple times when the component re-renders or the client is re-initialized.
     */
    useEffect(() => {
        if (!matrixClient) return;

        const handlePresence = (event) => presenceHandler(event, "setOwnPresence");
        const handleSyncPresence = (event) => presenceHandler(event, "sync");

        matrixClient.on(UserEvent.Presence, handlePresence);
        matrixClient.on(ClientEvent.Event, handleSyncPresence);

        return () => {
            matrixClient.removeListener(UserEvent.Presence, handlePresence);
            matrixClient.removeListener(ClientEvent.Event, handleSyncPresence);
        };
    }, [matrixClient]);

    useEffect(() => {
        if (!matrixClient || !isInitialSyncComplete ||
            matrixClient.__dailyfixmeSyncMode !== SLIDING_SYNC_MODE) {
            return;
        }

        let active = true;
        void hydrateKnownUserPresence(matrixClient, (presence, userId) => {
            if (active) {
                handleStatus(presence, userId);
            }
        });

        return () => {
            active = false;
        };
    }, [matrixClient, isInitialSyncComplete]);
    
    useEffect(() => {
        if (matrixClient && isInitialSyncComplete) {
            initializePresence();
        }
    }, [matrixClient, isInitialSyncComplete]);

    /**
     * This function is called by default for handling changes sent from matrix sync statuses
     * this allows us to be able to switch the status dot for the chat list and create chat modal list
     */
    const presenceHandler = (event, listenerType) => {
        if (listenerType === "setOwnPresence" && event.getType() === EventType.Presence) {
            const presenceEvent = event.getEffectiveEvent();
            if (!presenceEvent) return;
            const presence = presenceEvent.content.presence;
            const senderUserId = presenceEvent.sender;
            handleStatus(presence, senderUserId);
        }
        /* this section will make sure we're only monitoring statuses of other users
        ** doing so will prevent unintended overrides in the current user side of things
        */
        if (listenerType === "sync" && event.getType() === EventType.Presence) {
            const presenceEvent = event.getEffectiveEvent();
            if (!presenceEvent) return;
            const presence = presenceEvent.content.presence;
            const senderUserId = presenceEvent.sender;
            if (userContext[UserKeys.MATRIX_USER_ID] !== senderUserId) {
                handleStatus(presence, senderUserId);
            }
        }
    };

    const handleStatus = (status, matrixUserId) => {
        if (status === SetPresence.Offline) {
            setOnlineUsersMatrixId(onlineUsers => handleOfflinePresence(onlineUsers, matrixUserId));
        }
        else if (status === SetPresence.Online || status === SetPresence.Unavailable) {
            setOnlineUsersMatrixId(onlineUsers => handleOnlinePresence(onlineUsers, matrixUserId));
        }
    };

    /*
    **  setUserPresence is only called for the dropdown found in chatList.js
    **   as a onClick action passed down to UserAvatar.js
    */
    const setUserPresence = useCallback(async (onlineUsers, newPresenceStatus, matrixUserId, handleSettingCurrentUserStatus) => {
        const currentPresenceStatus = userContext[UserKeys.CHAT_PRESENCE_OVERRIDE];

        handleStatus(newPresenceStatus, matrixUserId);
        
        try {
            if (!matrixUserId) {
                throw new Error("[usePresenceListener] matrixUserId not found.");
            }

            const formData = {
                chatPresenceOverride: newPresenceStatus,
                matrixUserId: matrixUserId
            };
            const savePresenceUrl = `${API_URL}/chats/users/setPresenceOverride`;
            try {
                handleSettingCurrentUserStatus(newPresenceStatus);
                const result = await axios.post(savePresenceUrl, formData, headers);
                handleStatus(newPresenceStatus, onlineUsersMatrixId, matrixUserId);
                if (result.status !== 200) {
                    handleSettingCurrentUserStatus(currentPresenceStatus); //turn back the old status when error
                }
                // Only run when there's new employee need to add on shared room
                const canUpdateSharedRoom = isChatActionAllowed(CREATE_GROUP_CHAT, userContext[UserKeys.PERMISSIONS]);
                const url = `${API_URL}/chats/create/sharedRoom`;
                const needsUpdate = result.data.needsUpdate;
                if (needsUpdate && canUpdateSharedRoom) await axios.put(url, formData, headers);
            } catch (error) {
                return console.error("[usePresenceListener] Failed saving user presence.", error);
            }
        } catch (error) {
            return console.error("[usePresenceListener] An error occurred on setting the userPresence", error);
        }
    });

    const handleSetOwnUserPresence = async (status) => {
        setUserPresence(onlineUsersMatrixId, status, matrixClient.getUserId(), handleSettingCurrentUserStatus, userContext);
        if (status === SetPresence.Offline) {
            setClientPresenceToOffline();
        } else {
            setClientPresenceToOnline();
        }
    };

    const setClientPresenceToOffline = () => {

        /* {IPresenceOpts} presenceOpts */
        const presenceOpts = {
            presence: SetPresence.Offline,
            status_msg: ""
        };

        matrixClient.setSyncPresence(SetPresence.Offline);
        matrixClient.setPresence(presenceOpts);
    };

    const setClientPresenceToOnline = () => {
        /* {IPresenceOpts} presenceOpts */
        const presenceOpts = {
            presence: SetPresence.Online,
            status_msg: ""
        };

        matrixClient.setSyncPresence(SetPresence.Online);
        matrixClient.setPresence(presenceOpts);
    };

    return {
        onlineUsersMatrixId,
        handleSetOwnUserPresence
    };
};

export const hydrateKnownUserPresence = (matrixClient, handlePresence) => {
    const roomMemberUserIds = matrixClient.getRooms().flatMap(room =>
        room.getJoinedMembers().map(member => member.userId)
    );
    const knownUsers = new Map(matrixClient.getUsers().map(user => [user.userId, user]));
    const userIds = new Set([
        ...knownUsers.keys(),
        ...roomMemberUserIds,
    ]);

    userIds.forEach(userId => {
        if (!userId || userId === matrixClient.getUserId()) {
            return;
        }
        const user = knownUsers.get(userId) || matrixClient.getUser?.(userId);
        if (user?.presence) {
            handlePresence(user.presence, userId);
        }
    });
};

const getIndexFromOnlineUsersList = (onlineUsers, senderId) => {
    if (Array.isArray(onlineUsers)) {
        return onlineUsers.findIndex(userId => userId === senderId);
    }
};

const handleOnlinePresence = (matrixUserIds, matrixUserIdToAdd) => {
    if (!matrixUserIds) {
        console.warn("matrixUserIds is null or undefined!");
        return;
    }

    const index = getIndexFromOnlineUsersList(matrixUserIds, matrixUserIdToAdd);
    if (index === -1) {
        const newIds = [...matrixUserIds];
        newIds.push(matrixUserIdToAdd);
        return newIds;
    }
    return matrixUserIds;
};

const handleOfflinePresence = (matrixUserIds, matrixUserIdToRemove) => {
    if (!matrixUserIds) {
        console.warn("matrixUserIds is null or undefined!");
        return;
    }

    const index = getIndexFromOnlineUsersList(matrixUserIds, matrixUserIdToRemove);
    if (index !== -1) {
        const newIds = [...matrixUserIds];
        newIds.splice(index, 1);
        return newIds;
    }
    return matrixUserIds;
};
